import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert
} from '@mui/material';

interface AddTILModalProps {
  open: boolean;
  staffName: string;
  onClose: () => void;
  onConfirm: (hours: number, description: string, expiryDate: string) => void;
}

const AddTILModal: React.FC<AddTILModalProps> = ({
  open,
  staffName,
  onClose,
  onConfirm
}) => {
  const [hours, setHours] = useState<number>(0);
  const [description, setDescription] = useState('');
  const [expiryDate, setExpiryDate] = useState<string>('');
  const [errors, setErrors] = useState<{
    hours?: string;
    description?: string;
    expiryDate?: string;
  }>({});

  useEffect(() => {
    if (open) {
      // Reset form
      setHours(0);
      setDescription('');
      
      // Set default expiry to 1 year from today
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
      setExpiryDate(oneYearFromNow.toISOString().split('T')[0]);
      
      setErrors({});
    }
  }, [open]);

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (hours < 0.5) {
      newErrors.hours = 'Hours must be at least 0.5';
    } else if (hours > 24) {
      newErrors.hours = 'Hours cannot exceed 24';
    }

    if (description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    const expiry = new Date(expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (expiry <= today) {
      newErrors.expiryDate = 'Expiry date must be in the future';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validate()) {
      onConfirm(hours, description, expiryDate);
    }
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setHours(value);
    if (errors.hours) {
      setErrors({ ...errors, hours: undefined });
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
    if (errors.description && e.target.value.trim().length >= 10) {
      setErrors({ ...errors, description: undefined });
    }
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryDate(e.target.value);
    if (errors.expiryDate) {
      setErrors({ ...errors, expiryDate: undefined });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper'
        }
      }}
    >
      <DialogTitle>
        Add TIL Hours
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Adding TIL hours for <strong>{staffName}</strong>
          </Alert>

          <TextField
            fullWidth
            type="number"
            label="Hours"
            value={hours}
            onChange={handleHoursChange}
            error={!!errors.hours}
            helperText={errors.hours || 'Enter hours between 0.5 and 24'}
            inputProps={{ min: 0.5, max: 24, step: 0.5 }}
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={description}
            onChange={handleDescriptionChange}
            error={!!errors.description}
            helperText={errors.description || `${description.length} characters (minimum 10)`}
            placeholder="e.g., Overtime worked during festival setup on 15th June"
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            type="date"
            label="Expiry Date"
            value={expiryDate}
            onChange={handleExpiryChange}
            error={!!errors.expiryDate}
            helperText={errors.expiryDate || 'TIL hours expire on this date'}
            InputLabelProps={{ shrink: true }}
            required
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!hours || !description.trim() || !expiryDate}
        >
          Add TIL
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTILModal;